import { KeyValuePair } from './conversation.interface';

/** Statuses Meta documents for a Messenger `appointment_booking` attachment. */
export enum AppointmentStatus {
  Requested = 'requested',
  Confirmed = 'confirmed',
  Declined = 'declined',
  Cancelled = 'cancelled',
}

export const APPOINTMENT_STATUS_METADATA_KEY = 'appointmentStatus';
export const APPOINTMENT_START_TIME_METADATA_KEY = 'appointmentStartTime';
export const APPOINTMENT_END_TIME_METADATA_KEY = 'appointmentEndTime';
export const APPOINTMENT_TIMEZONE_METADATA_KEY = 'appointmentTimezone';

const APPOINTMENT_METADATA_KEYS = [
  APPOINTMENT_STATUS_METADATA_KEY,
  APPOINTMENT_START_TIME_METADATA_KEY,
  APPOINTMENT_END_TIME_METADATA_KEY,
  APPOINTMENT_TIMEZONE_METADATA_KEY,
];

// Per-status keys, so the heading is translated in the UI rather than read from the
// (English-only) server-authored message body — appointment messages arrive with an empty body.
export const APPOINTMENT_STATUS_MESSAGE_KEYS = {
  requested: 'INBOX.CHAT.APPOINTMENT.REQUESTED',
  updated: 'INBOX.CHAT.APPOINTMENT.UPDATED',
} as const;

export interface AppointmentBooking {
  status: string;
  /** Unix seconds, absent when the sending channel omitted it. */
  startTime?: number;
  /** Unix seconds. Carried for a future start–end range; not rendered today. */
  endTime?: number;
  /** IANA name, e.g. `America/Chicago`. */
  timezone?: string;
}

export function isAppointmentMessage(metadata?: KeyValuePair[]): boolean {
  return !!metadata?.some((m) => APPOINTMENT_METADATA_KEYS.includes(m.key));
}

export function getAppointmentBooking(metadata?: KeyValuePair[]): AppointmentBooking | undefined {
  if (!isAppointmentMessage(metadata)) {
    return undefined;
  }
  return {
    status: readValue(metadata, APPOINTMENT_STATUS_METADATA_KEY) ?? '',
    startTime: readUnixSeconds(metadata, APPOINTMENT_START_TIME_METADATA_KEY),
    endTime: readUnixSeconds(metadata, APPOINTMENT_END_TIME_METADATA_KEY),
    timezone: readValue(metadata, APPOINTMENT_TIMEZONE_METADATA_KEY) || undefined,
  };
}

/**
 * Only `requested` gets its own heading. Every other status — including one this build does not
 * know and a booking that arrived with no status at all — falls back to the generic update copy.
 */
export function getAppointmentStatusKey(status?: string): string {
  return status === AppointmentStatus.Requested
    ? APPOINTMENT_STATUS_MESSAGE_KEYS.requested
    : APPOINTMENT_STATUS_MESSAGE_KEYS.updated;
}

function readValue(metadata: KeyValuePair[] | undefined, key: string): string | undefined {
  return metadata?.find((m) => m.key === key)?.value;
}

function readUnixSeconds(metadata: KeyValuePair[] | undefined, key: string): number | undefined {
  const raw = readValue(metadata, key);
  if (!raw) {
    return undefined;
  }
  const seconds = Number(raw);
  return Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}
